import styled from "styled-components";
import {motion} from "framer-motion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SmartImage from "../../../lib/UIBookBrd/SmartImage";
import {down, eyebrow, focusRing} from "../../../Styles/media";

const PANEL_WIDTH = 430

/* ------------------------------------------------------------------- shell */

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: ${PANEL_WIDTH}px;
    max-width: 100vw;

    ${down('xs')} {
        width: 100vw;
    }
`

const DrawerTitle = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${({theme}) => theme.color};
`

const DrawerTitleCount = styled.span`
    ${eyebrow};
    font-size: 10.5px;
`

/* -------------------------------------------------------------- empty state */

const Empty = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: clamp(48px, 14vh, 96px) 28px;
    text-align: center;
`

const EmptyIconRing = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 76px;
    margin-bottom: 6px;
    border: 1px solid ${({theme}) => theme.line};
    border-radius: 50%;
    background: radial-gradient(70% 70% at 50% 40%, rgba(214, 218, 225, 0.12), transparent 70%);
`

const EmptyIcon = styled(FontAwesomeIcon)`
    width: 26px;
    height: 26px;
    color: ${({theme}) => theme.color};
`

const EmptyTitle = styled.h2`
    font-size: 20px;
    color: ${({theme}) => theme.color};
`

const EmptyText = styled.p`
    max-width: 34ch;
    font-size: 14px;
    line-height: 1.6;
    color: ${({theme}) => theme.colorMuted};
`

/* -------------------------------------------------------------------- items */

const Items = styled.ul`
    display: flex;
    flex-direction: column;
    list-style: none;
`

const Item = styled(motion.li)`
    position: relative;
    display: flex;
    gap: 14px;
    padding: 16px 18px;
    border-bottom: 1px solid ${({theme}) => theme.lineFaint};
`

const ItemImage = styled(SmartImage)`
    width: 92px;
    height: 92px;
    flex-shrink: 0;
    border: 1px solid ${({theme}) => theme.lineFaint};
    border-radius: ${({theme}) => theme.radius.sm};
    padding: 8px;
    background: radial-gradient(70% 70% at 50% 40%, rgba(255, 255, 255, 0.06), transparent 70%);
`

const ItemBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;
`

const ItemProducer = styled.span`
    ${eyebrow};
    font-size: 9.5px;
`

const ItemName = styled.span`
    font-size: 15.5px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: ${({theme}) => theme.color};
    padding-right: 24px;
`

const ItemUnit = styled.span`
    font-size: 12.5px;
    color: ${({theme}) => theme.colorFaint};
`

const ItemBottom = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
`

const ItemTotal = styled.span`
    margin-left: auto;
    font-size: 15px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${({theme}) => theme.color};
`

const RemoveButton = styled.button`
    ${focusRing};
    position: absolute;
    top: 14px;
    right: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    color: ${({theme}) => theme.colorFaint};
    transition: color 200ms ${({theme}) => theme.ease.out}, background-color 200ms ${({theme}) => theme.ease.out};

    &:hover {
        color: ${({theme}) => theme.danger};
        background-color: rgba(255, 79, 79, 0.12);
    }
`

const RemoveIcon = styled(FontAwesomeIcon)`
    width: 12px;
    height: 12px;
`

/* --------------------------------------------------------------------- form */

const Form = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 22px 18px 26px;
`

const FormHead = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`

const FormTitle = styled.h3`
    font-size: 17px;
    letter-spacing: -0.01em;
    color: ${({theme}) => theme.color};
`

const FormLead = styled.p`
    font-size: 13px;
    line-height: 1.55;
    color: ${({theme}) => theme.colorMuted};
`

const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const FieldLabel = styled.span`
    ${eyebrow};
    font-size: 10px;
`

const inputStyles = `
    width: 100%;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 14.5px;
    outline: none;
    transition: border-color 220ms ease, background-color 220ms ease;
`

const Input = styled.input<{ $error: boolean }>`
    ${inputStyles};
    border: 1px solid ${({theme, $error}) => ($error ? theme.danger : theme.line)};
    background-color: rgba(255, 255, 255, 0.035);
    color: ${({theme}) => theme.color};

    &::placeholder {
        color: ${({theme}) => theme.colorFaint};
    }

    &:focus {
        border-color: ${({theme, $error}) => ($error ? theme.danger : theme.accent)};
        background-color: rgba(255, 255, 255, 0.055);
    }
`

const TextArea = styled.textarea<{ $error: boolean }>`
    ${inputStyles};
    min-height: 92px;
    resize: vertical;
    font-family: inherit;
    border: 1px solid ${({theme, $error}) => ($error ? theme.danger : theme.line)};
    background-color: rgba(255, 255, 255, 0.035);
    color: ${({theme}) => theme.color};

    &::placeholder {
        color: ${({theme}) => theme.colorFaint};
    }

    &:focus {
        border-color: ${({theme, $error}) => ($error ? theme.danger : theme.accent)};
        background-color: rgba(255, 255, 255, 0.055);
    }
`

const FieldError = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: ${({theme}) => theme.danger};
`

const FieldErrorIcon = styled(FontAwesomeIcon)`
    width: 11px;
    height: 11px;
`

const Alert = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid ${({theme}) => theme.danger};
    border-radius: ${({theme}) => theme.radius.sm};
    background-color: rgba(255, 79, 79, 0.1);
    font-size: 13px;
    line-height: 1.5;
    color: ${({theme}) => theme.color};
`

const Disclaimer = styled.details`
    border-top: 1px solid ${({theme}) => theme.lineFaint};
    padding-top: 14px;
`

const DisclaimerSummary = styled.summary`
    ${focusRing};
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    list-style: none;
    font-size: 12px;
    font-weight: 600;
    color: ${({theme}) => theme.colorFaint};

    &::-webkit-details-marker {
        display: none;
    }

    &:hover {
        color: ${({theme}) => theme.colorMuted};
    }
`

const DisclaimerIcon = styled(FontAwesomeIcon)`
    width: 11px;
    height: 11px;
`

const DisclaimerText = styled.p`
    margin-top: 10px;
    font-size: 11.5px;
    line-height: 1.65;
    white-space: pre-line;
    color: ${({theme}) => theme.colorFaint};
`

/* ------------------------------------------------------------------ footer */

const Footer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 16px 18px 18px;
`

const TotalRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
`

const TotalLabel = styled.span`
    ${eyebrow};
    font-size: 10px;
`

const TotalValue = styled.span`
    margin-left: auto;
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: ${({theme}) => theme.color};
`

const TotalUnit = styled.span`
    font-size: 12.5px;
    font-weight: 500;
    color: ${({theme}) => theme.colorFaint};
`

const RequestNote = styled.span`
    font-size: 11.5px;
    color: ${({theme}) => theme.colorFaint};
`

/* ----------------------------------------------------------------- success */

const Success = styled(motion.div)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: clamp(48px, 14vh, 96px) 28px;
    text-align: center;
`

const SuccessRing = styled(motion.div)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    border: 1px solid rgba(53, 196, 106, 0.5);
    background: radial-gradient(70% 70% at 50% 40%, rgba(53, 196, 106, 0.22), transparent 70%);
`

const SuccessIcon = styled(FontAwesomeIcon)`
    width: 30px;
    height: 30px;
    color: ${({theme}) => theme.success};
`

const SuccessTitle = styled.h2`
    font-size: 21px;
    color: ${({theme}) => theme.color};
`

const SuccessText = styled.p`
    max-width: 34ch;
    font-size: 14px;
    line-height: 1.6;
    color: ${({theme}) => theme.colorMuted};
`

export const Styled = {
    Content,
    DrawerTitle,
    DrawerTitleCount,

    Empty,
    EmptyIconRing,
    EmptyIcon,
    EmptyTitle,
    EmptyText,

    Items,
    Item,
    ItemImage,
    ItemBody,
    ItemProducer,
    ItemName,
    ItemUnit,
    ItemBottom,
    ItemTotal,
    RemoveButton,
    RemoveIcon,

    Form,
    FormHead,
    FormTitle,
    FormLead,
    Field,
    FieldLabel,
    Input,
    TextArea,
    FieldError,
    FieldErrorIcon,
    Alert,
    Disclaimer,
    DisclaimerSummary,
    DisclaimerIcon,
    DisclaimerText,

    Footer,
    TotalRow,
    TotalLabel,
    TotalValue,
    TotalUnit,
    RequestNote,

    Success,
    SuccessRing,
    SuccessIcon,
    SuccessTitle,
    SuccessText
}
